import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { FleetMap } from "./components/FleetMap";
import { NodePanel } from "./components/NodePanel";
import { PilotoBarra } from "./components/PilotoBarra";
import { PilotoPicker } from "./components/PilotoPicker";
import { INTERVALO_INICIAL, PERSONALIZADO, intervaloDe, type Intervalo } from "./components/RangePicker";
import { useFleet } from "./hooks/useFleet";
import { usePiloto } from "./hooks/usePiloto";
import { usePositions } from "./hooks/usePositions";
import { ROTULO_FRESCURA, frescura, haQuantoTempo, type Frescura } from "./lib/format";
import { pilotoPorId } from "./lib/pilotos";

/** De quanto em quanto tempo a janela dos atalhos desliza. */
const AO_VIVO_MS = 30_000;

/** Quantas horas vale cada atalho, para o recalcular ao deslizar. */
const HORAS: Record<string, number> = { "1 h": 1, "6 h": 6, "24 h": 24, "7 d": 168 };

/** Rótulo da janela em modo piloto. Nunca coincide com um atalho nem com o livre. */
const ROTULO_PILOTO = "piloto";

/** Lê e escreve a seleção no URL, para o link ser partilhável e o F5 aguentar. */
function useSelecao(): {
  code: string | null;
  comparadas: string[];
  piloto: string | null;
  selecionar: (code: string | null) => void;
  alternarComparada: (code: string) => void;
  escolherPiloto: (id: string | null) => void;
} {
  const ler = (): { code: string | null; comparadas: string[]; piloto: string | null } => {
    const params = new URLSearchParams(window.location.search);
    const vs = params.get("vs");
    return {
      code: params.get("node"),
      comparadas: vs === null || vs === "" ? [] : vs.split(","),
      piloto: params.get("piloto"),
    };
  };

  const [estado, setEstado] = useState(ler);

  const escrever = useCallback(
    (novo: { code: string | null; comparadas: string[]; piloto: string | null }) => {
      const params = new URLSearchParams(window.location.search);
      if (novo.code === null) params.delete("node");
      else params.set("node", novo.code);
      if (novo.comparadas.length === 0) params.delete("vs");
      else params.set("vs", novo.comparadas.join(","));
      if (novo.piloto === null) params.delete("piloto");
      else params.set("piloto", novo.piloto);

      const query = params.toString();
      window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
      setEstado(novo);
    },
    [],
  );

  const selecionar = useCallback(
    (code: string | null) => {
      // Fechar o painel também limpa a comparação: sem boia principal não há
      // gráficos onde as outras apareçam, e um `vs=` órfão no URL confundia.
      escrever(
        code === null
          ? { code: null, comparadas: [], piloto: estado.piloto }
          : { code, comparadas: [], piloto: estado.piloto },
      );
    },
    [escrever, estado.piloto],
  );

  // Calculado fora do `setState`: um updater tem de ser puro, e o `replaceState`
  // lá dentro corria duas vezes em StrictMode.
  const alternarComparada = useCallback(
    (code: string) => {
      if (code === estado.code) return;
      const novas = estado.comparadas.includes(code)
        ? estado.comparadas.filter((c) => c !== code)
        : [...estado.comparadas, code];
      escrever({ code: estado.code, comparadas: novas, piloto: estado.piloto });
    },
    [estado, escrever],
  );

  // Entrar ou sair de um piloto limpa a seleção: a boia aberta ao vivo pode nem
  // fazer parte do elenco, e ao sair um painel preso na janela antiga mentia.
  const escolherPiloto = useCallback(
    (id: string | null) => {
      escrever({ code: null, comparadas: [], piloto: id });
    },
    [escrever],
  );

  useEffect(() => {
    const aoVoltar = (): void => setEstado(ler());
    window.addEventListener("popstate", aoVoltar);
    return () => window.removeEventListener("popstate", aoVoltar);
  }, []);

  return { ...estado, selecionar, alternarComparada, escolherPiloto };
}

export function App(): ReactNode {
  const { frota, erro, aRecarregar } = useFleet();
  const {
    code: selecionado,
    comparadas,
    piloto: pilotoId,
    selecionar,
    alternarComparada,
    escolherPiloto,
  } = useSelecao();
  const [intervalo, setIntervalo] = useState<Intervalo>(INTERVALO_INICIAL);
  const [aoVivo, setAoVivo] = useState(true);

  // Um id desconhecido no URL cai fora do modo piloto em silêncio: o registo é
  // a única fonte de pilotos, e inventar uma janela a partir do id era pior.
  const piloto = pilotoPorId(pilotoId);
  const dadosPiloto = usePiloto(piloto);

  // Cursor do replay. Começa no **fim** — o retrato completo, com as mortes
  // todas no mapa — e recua a partir daí; tocar ▶ do fim recomeça do zero.
  const [instante, setInstante] = useState<number | null>(null);
  useEffect(() => {
    setInstante(piloto === null ? null : Date.parse(piloto.ate));
  }, [piloto]);

  // Em modo piloto a janela dos gráficos é a do piloto, sempre. O intervalo
  // escolhido ao vivo fica guardado intacto para quando se sair.
  const intervaloPiloto = useMemo<Intervalo | null>(
    () =>
      piloto === null
        ? null
        : { rotulo: ROTULO_PILOTO, de: new Date(piloto.de), ate: new Date(piloto.ate) },
    [piloto],
  );
  const intervaloEfetivo = intervaloPiloto ?? intervalo;

  const nos = frota?.nodes ?? [];
  const no = nos.find((n) => n.code === selecionado) ?? null;
  // Em modo piloto o rasto da selecionada vem da camada do piloto, não daqui.
  const rasto = usePositions(piloto === null ? selecionado : null, intervaloEfetivo.de, intervaloEfetivo.ate);

  /**
   * Modo ao vivo: os atalhos são relativos, portanto a janela desliza sozinha.
   *
   * O intervalo à escolha **nunca** se mexe — é absoluto por definição, e um
   * intervalo que saltasse debaixo de quem está a olhar para ele era pior que
   * inútil. Daí a condição ser o rótulo, não uma opção à parte. Um piloto é
   * passado fechado: nada desliza.
   */
  const relativo = intervalo.rotulo !== PERSONALIZADO;
  useEffect(() => {
    if (piloto !== null || !aoVivo || !relativo) return;
    const horas = HORAS[intervalo.rotulo];
    if (horas === undefined) return;

    const t = setInterval(() => setIntervalo(intervaloDe(intervalo.rotulo, horas)), AO_VIVO_MS);
    return () => clearInterval(t);
  }, [piloto, aoVivo, relativo, intervalo.rotulo]);

  // Contagem por estado. Serve de legenda das cores do mapa -- que assim nunca
  // aparecem sem o nome do estado ao lado.
  const contagem = useMemo(() => {
    const c: Record<Frescura, number> = { boa: 0, aviso: 0, critica: 0 };
    for (const n of nos) c[frescura(n)] += 1;
    return c;
  }, [nos]);

  const ultimoContacto = useMemo(() => {
    const instantes = nos
      .map((n) => n.last_contact)
      .filter((x): x is string => x !== null)
      .map(Date.parse);
    return instantes.length > 0 ? new Date(Math.max(...instantes)).toISOString() : null;
  }, [nos]);

  return (
    <div className="app">
      <header className="topo">
        <h1>AquaScan</h1>
        <span className="local">Estuário do Rio Lima · Viana do Castelo</span>

        <div className="resumo">
          {/* A legenda de frescura é do modo ao vivo: em replay os marcadores
              dizem vivo/morto no instante do cursor, não há "atrasada". */}
          {piloto === null &&
            (["boa", "aviso", "critica"] as const).map((estado) => (
              <span className="distintivo" key={estado}>
                <i className={`ponto ${estado}`} aria-hidden="true" />
                {contagem[estado]} {ROTULO_FRESCURA[estado]}
              </span>
            ))}
          {piloto === null && (
            <span className="local">
              {ultimoContacto === null
                ? "sem contacto"
                : `último lote ${haQuantoTempo(ultimoContacto)}`}
            </span>
          )}
          <PilotoPicker ativo={piloto} aoEscolher={escolherPiloto} />
          {/* Três estados, não dois: com datas à escolha isto não está "em
              pausa", está fora de questão — o intervalo é absoluto. Dizer
              "pausa" sugeria que havia um botão para o retomar. */}
          <button
            type="button"
            className={piloto === null && aoVivo && relativo ? "ao-vivo ativo" : "ao-vivo"}
            aria-pressed={piloto === null && relativo ? aoVivo : false}
            disabled={piloto !== null || !relativo}
            title={
              piloto !== null
                ? "A ver um piloto anterior — sai do piloto para voltar ao vivo"
                : relativo
                  ? "A janela dos atalhos desliza de 30 em 30 s"
                  : "Datas à escolha: o intervalo é absoluto e não desliza"
            }
            onClick={() => setAoVivo((v) => !v)}
          >
            <i aria-hidden="true" />
            {piloto !== null ? "replay" : !relativo ? "intervalo fixo" : aoVivo ? "ao vivo" : "em pausa"}
          </button>
        </div>
      </header>

      {erro !== null && (
        <p className="aviso-erro" style={{ margin: "8px 16px 0" }} role="alert">
          {erro} — a mostrar o último estado conhecido.
        </p>
      )}

      <div className="corpo">
        <div className={aRecarregar && frota === null ? "a-recarregar" : undefined}>
          <FleetMap
            nos={nos}
            selecionado={selecionado}
            rasto={rasto}
            aoSelecionar={selecionar}
            piloto={
              piloto !== null && instante !== null
                ? { def: piloto, dados: dadosPiloto, instante }
                : null
            }
          />
        </div>

        {piloto !== null && instante !== null && (
          <PilotoBarra
            piloto={piloto}
            dados={dadosPiloto}
            instante={instante}
            aoMudarInstante={setInstante}
            selecionado={selecionado}
            aoSelecionar={selecionar}
          />
        )}

        {no !== null && (
          <NodePanel
            no={no}
            nos={nos}
            comparadas={comparadas}
            aoAlternarComparada={alternarComparada}
            intervalo={intervaloEfetivo}
            aoMudarIntervalo={setIntervalo}
            aoFechar={() => selecionar(null)}
            piloto={piloto}
          />
        )}
      </div>
    </div>
  );
}
